import React, { useState, useEffect } from "react";
import { Palette, Eye, Upload, Info, Image, X } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import ToggleSetting from "../../components/ui/ToggleSetting";
import FormField from "../../components/ui/FormField";
import {
  getWebsiteSettings,
  updateThemeSettings,
} from "../../services/websitesetting";
import { validateImage } from "../../lib/image";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

const FrontThemeSettings = () => {
  const { user } = useAuth();
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const [logoVisibility, setLogoVisibility] = useState(true);
  const [websiteTitle, setWebsiteTitle] = useState("");
  const [themeId, setThemeId] = useState<number | null>(null);
  const [logoFile, setLogoFile] = useState<string | null>(null);
  const [faviconFile, setFaviconFile] = useState<string | null>(null);
  const [logoUpload, setLogoUpload] = useState<File | null>(null);
  const [faviconUpload, setFaviconUpload] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState("#885ABB");
  const [secondaryColor, setSecondaryColor] = useState("#FFFFFF");
  const [carouselImages, setCarouselImages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const settings = await getWebsiteSettings();
        if (settings) {
          const theme = settings.website_theme_settings;
          const carousel_images = settings.images;

          if (theme) {
            setThemeId(theme.id);
            setWebsiteTitle(theme.title || "");
            setLogoVisibility(Number(theme.is_logo_visible) === 1);
            setPrimaryColor(theme.primary_color || "#885ABB");
            setSecondaryColor(theme.secondary_color || "#FFFFFF");

            if (theme.logo) {
              setLogoFile(
                theme.logo.startsWith("http")
                  ? theme.logo
                  : `https://salonify.co.uk/user-uploads/front-logo/${theme.logo}`
              );
            }

            if (theme.favicon) {
              setFaviconFile(
                theme.favicon.startsWith("http")
                  ? theme.favicon
                  : `https://salonify.co.uk/user-uploads/favicon/${theme.favicon}`
              );
            }
          }

          if (carousel_images) {
            setCarouselImages(carousel_images);
          }
        }
      } catch (err: any) {
        console.error("fetchData ERROR:", err);
        toast.error("Failed to load theme settings. Please refresh.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await validateImage(file);
        if (file.size > 2 * 1024 * 1024) {
          throw new Error("Logo size should be less than 2MB");
        }
        setLogoUpload(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoFile(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        setErrors((prev) => ({ ...prev, logo: "" }));
      } catch (err: any) {
        toast.error(err.message || "Invalid image");
        setErrors((prev) => ({
          ...prev,
          logo: err.message || "Invalid image",
        }));
      }
    }
  };

  const handleFaviconUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await validateImage(file);
        if (file.size > 1024 * 1024) {
          throw new Error("Favicon size should be less than 1MB");
        }
        setFaviconUpload(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setFaviconFile(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        setErrors((prev) => ({ ...prev, favicon: "" }));
      } catch (err: any) {
        toast.error(err.message || "Invalid image");
        setErrors((prev) => ({
          ...prev,
          favicon: err.message || "Invalid image",
        }));
      }
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!websiteTitle.trim()) {
      newErrors.title = "Website title is required";
    } else if (websiteTitle.length > 50) {
      newErrors.title = "Title cannot exceed 50 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (!themeId) {
        toast.error("Theme configuration not loaded properly.");
        return;
      }
      await updateThemeSettings(themeId, {
        title: websiteTitle,
        is_logo_visible: logoVisibility,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        logo: logoUpload,
        favicon: faviconUpload,
      });
      toast.success("Front theme updated successfully!");
    } catch (error: any) {
      console.error("handleSave ERROR:", error);
      console.error("handleSave Response:", error.response);
      console.error("handleSave Response Data:", error.response?.data);
      if (error.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Validation failed. Please check the fields.");
      } else {
        const status = error.response?.status;
        let userMessage = "Something went wrong while saving settings.";

        if (status === 404) {
          userMessage =
            "The theme settings could not be found. Please refresh and try again.";
        } else if (status === 403) {
          userMessage = "You do not have permission to update these settings.";
        } else if (status === 413) {
          userMessage =
            "The uploaded file is too large for the server to process.";
        } else if (status === 500) {
          userMessage = "Server error occurred. Our team has been notified.";
        } else if (error.message === "Network Error") {
          userMessage =
            "Connection lost. Please check your internet and try again.";
        }
        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={true}
          fullScreen={false}
          description={
            isSubmitting
              ? "Updating branding settings..."
              : "Loading theme settings..."
          }
        />
      )}
      <div className="space-y-5">
        {/* Header */}
        <PageHeader
          title="Front Theme Settings"
          description="Customize your website's branding and appearance"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={<ViewWebsiteButton subdomain={subdomain} />}
        />

        {/* Main Content */}
        <WebsiteSectionCard title="Branding & Theme" icon={Palette}>
          <div className="space-y-5">
            {/* Website Title Section */}
            <div>
              <SectionHeader title="Website Information" />

              <InfoContainer
                message="Set your website title and branding elements"
                iconSize={13}
                className="mb-4"
              />

              <FormField
                label="Website Title *"
                name="websiteTitle"
                value={websiteTitle}
                onChange={(name, value) => {
                  setWebsiteTitle(value);
                  if (errors.title)
                    setErrors((prev) => ({ ...prev, title: "" }));
                }}
                type="text"
                placeholder="Enter website title"
                className="max-w-xl"
                error={errors.title || errors.website_title}
              />
              <p className="text-xs text-gray-500 mt-1">
                This will appear in browser tabs and search results
              </p>
            </div>

            {/* 
            <div>
              <SectionHeader title="Theme Colors" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-10 w-20 cursor-pointer rounded border border-gray-200"
                    />
                    <input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="flex-1 h-10 px-3 text-sm border border-gray-200 rounded-md uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="h-10 w-20 cursor-pointer rounded border border-gray-200"
                    />
                    <input
                      type="text"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="flex-1 h-10 px-3 text-sm border border-gray-200 rounded-md uppercase"
                    />
                  </div>
                </div>
              </div>
            </div>
            */}

            {/* Logo & Favicon Section */}
            <div>
              <SectionHeader title="Logo" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`border-2 border-dashed rounded-lg h-48 flex flex-col items-center justify-center cursor-pointer transition-all group ${
                        errors.logo || errors.title
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-[#885ABB] bg-gray-50"
                      }`}
                    >
                      {logoFile ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={logoFile}
                            alt="Logo preview"
                            className="max-h-40 max-w-full object-contain"
                          />
                          <button
                            onClick={(e) => {
                              console.log("Removing logo...");
                              e.preventDefault();
                              e.stopPropagation();
                              setLogoFile(null);
                              setLogoUpload(null);
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 z-10"
                            title="Remove image"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload
                            className={`h-12 w-12 mb-2 transition-colors ${
                              errors.logo
                                ? "text-red-400"
                                : "text-gray-400 group-hover:text-[#885ABB]"
                            }`}
                          />
                          <p className="text-gray-600 text-sm font-medium">
                            Click to upload logo
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            or drag and drop
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                  {errors.logo && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                      <span className="font-medium">Error:</span> {errors.logo}
                    </p>
                  )}
                  {!errors.logo && (
                    <p className="text-xs mt-2 flex items-center gap-1 text-gray-500">
                      <Info size={11} />
                      Maximum dimension: 150 × 150 pixels
                    </p>
                  )}
                </div>

                {/* Favicon Upload 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Favicon
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      className="hidden"
                      id="favicon-upload"
                    />
                    <label
                      htmlFor="favicon-upload"
                      className={`border-2 border-dashed rounded-lg h-48 flex flex-col items-center justify-center cursor-pointer transition-all group ${errors.favicon ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-[#885ABB] bg-gray-50'
                        }`}
                    >
                      {faviconFile ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={faviconFile}
                            alt="Favicon preview"
                            className="max-h-40 max-w-full object-contain"
                          />
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFaviconFile(null);
                              setFaviconUpload(null);
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 z-10"
                            title="Remove image"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Image className={`h-12 w-12 mb-2 transition-colors ${errors.favicon ? 'text-red-400' : 'text-gray-400 group-hover:text-[#885ABB]'}`} />
                          <p className="text-gray-600 text-sm font-medium">
                            Click to upload favicon
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            or drag and drop
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                  {errors.favicon && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                      <span className="font-medium">Error:</span> {errors.favicon}
                    </p>
                  )}
                  {!errors.favicon && (
                    <p className="text-xs mt-2 flex items-center gap-1 text-gray-500">
                      <Info size={11} />
                      Maximum dimension: 32 × 32 pixels recommended
                    </p>
                  )}
                </div>
                */}
              </div>
            </div>

            {/* Logo Visibility Section */}
            <div>
              <SectionHeader title="Display Settings" />

              <ToggleSetting
                label="Logo Visibility"
                description="Show or hide logo on your website"
                checked={logoVisibility}
                onChange={setLogoVisibility}
              />
            </div>

            {/* Carousel Images Section 
            {carouselImages.length > 0 && (
              <div>
                <SectionHeader title="Slider Images" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {carouselImages.map((img: any) => (
                    <div key={img.id} className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 group">
                      <img
                        src={img.image_url}
                        alt="Slider"
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-[10px] font-medium px-2 py-1 bg-black/50 rounded">ID: {img.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            */}

            {/* Save Button */}
            <div className="pt-2 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={isSubmitting}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-6 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default FrontThemeSettings;
